import { useEffect, useLayoutEffect, useState, useRef, type Ref } from 'react';
import { Popover } from 'react-tiny-popover';
import RichText from 'components/rich-text';
import { ScrollableContainer } from '../scrollable-container';
import styles from './hotspot.module.scss';
import type { PortableTextBlock } from '@portabletext/types';

const ARROW_EDGE_GUTTER_PX = 10;
const ARROW_SIZE_PX = 10;
const VIEWPORT_INSET_PX = 10;

export function HotSpot({ isMobile, ...restProps }: HotSpotProps) {
	if (isMobile) {
		return <HotSpotMobile {...restProps} />;
	}

	return <HotSpotDesktop {...restProps} />;
}

function HotSpotMobile({ id, isActiveComment }: HotSpotVariantProps) {
	return <HotSpotButton id={id} popupExpanded={isActiveComment} />;
}

function HotSpotDesktop({
	id,
	content,
	citation,
	popoverBoundaryElement,
	containerEl,
	isActiveComment,
}: HotSpotVariantProps) {
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const popoverTriggerElRef = useRef<HTMLDivElement>(null);
	const popoverContentRef = useRef<HTMLDivElement>(null);
	const popoverMaxHeightRef = useRef<number>(null);

	useEffect(() => {
		setIsPopoverOpen(isActiveComment);
	}, [isActiveComment]);

	// Prevent clicks inside the popover content from closing it.
	useEffect(() => {
		const el = popoverContentRef.current;
		if (!el) return;

		const handleMouseDown = (e: MouseEvent) => {
			e.stopPropagation();
		};
		el.addEventListener('mousedown', handleMouseDown);
		return () => el.removeEventListener('mousedown', handleMouseDown);
	}, [isPopoverOpen]);

	// Close the popover if the hotspot scrolls completely out of view.
	useLayoutEffect(() => {
		if (!popoverBoundaryElement || !containerEl) return;
		if (typeof IntersectionObserver === 'undefined') return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (!isPopoverOpen) return;

				const entry = entries[0];
				if (entry.intersectionRatio === 0) {
					setIsPopoverOpen(false);
				}
			},
			{
				root: popoverBoundaryElement,
				threshold: [0, 1],
			},
		);
		observer.observe(containerEl);
		return () => observer.disconnect();
	}, [isPopoverOpen, popoverBoundaryElement, containerEl]);

	// Limit the popover's height to ensure we always observe
	// the viewport inset applied to smaller popovers.
	useLayoutEffect(() => {
		if (!popoverBoundaryElement) return;
		const observer = new ResizeObserver(() => {
			const { height } = popoverBoundaryElement.getBoundingClientRect();
			popoverMaxHeightRef.current = height - VIEWPORT_INSET_PX * 2;
		});
		observer.observe(popoverBoundaryElement);
		return () => observer.disconnect();
	}, [popoverBoundaryElement]);

	return (
		<Popover
			containerClassName={styles.popoverContainer}
			isOpen={isPopoverOpen}
			padding={50}
			reposition={true}
			positions={['left', 'right', 'top', 'bottom']}
			boundaryInset={VIEWPORT_INSET_PX}
			boundaryElement={popoverBoundaryElement}
			ref={popoverTriggerElRef}
			content={({ position, childRect, popoverRect }) => {
				// Reimplemented positioning logic to add a gutter along the edges so that it doesn't overlap
				// the rounded corners, causing some visual jank.
				//
				// https://github.com/alexkatz/react-tiny-popover/blob/main/src/useArrowContainer.ts
				const lowerBound = ARROW_SIZE_PX + ARROW_EDGE_GUTTER_PX;
				const leftUpperBound =
					popoverRect.width - (ARROW_SIZE_PX + ARROW_EDGE_GUTTER_PX);
				const topUpperBound =
					popoverRect.height - (ARROW_SIZE_PX + ARROW_EDGE_GUTTER_PX);

				let left =
					childRect.left -
					popoverRect.left +
					childRect.width / 2 -
					ARROW_SIZE_PX;
				left = left < lowerBound ? lowerBound : left;
				left =
					left + ARROW_SIZE_PX * 2 > leftUpperBound
						? leftUpperBound - ARROW_SIZE_PX * 2
						: left;
				left = Number.isNaN(left) ? 0 : left;

				let top =
					childRect.top -
					popoverRect.top +
					childRect.height / 2 -
					ARROW_SIZE_PX;
				top = top < lowerBound ? lowerBound : top;
				top =
					top + ARROW_SIZE_PX * 2 > topUpperBound
						? topUpperBound - ARROW_SIZE_PX * 2
						: top;
				top = Number.isNaN(top) ? 0 : top;

				// By overlapping the arrow and the popover by 1px, we can ensure there is no gap
				// visible to the user even if a rounded subpixel calculation were to create a gap.
				const arrowOffset = ARROW_SIZE_PX - 1;

				let arrowStyles: React.CSSProperties = {};
				if (position === 'right') {
					arrowStyles = {
						borderTop: `${ARROW_SIZE_PX}px solid transparent`,
						borderBottom: `${ARROW_SIZE_PX}px solid transparent`,
						borderRight: `${ARROW_SIZE_PX}px solid var(--color-site-background)`,
						left: arrowOffset * -1,
						top,
					};
				}
				if (position === 'left') {
					arrowStyles = {
						borderTop: `${ARROW_SIZE_PX}px solid transparent`,
						borderBottom: `${ARROW_SIZE_PX}px solid transparent`,
						borderLeft: `${ARROW_SIZE_PX}px solid var(--color-site-background)`,
						right: arrowOffset * -1,
						top,
					};
				}
				if (position === 'bottom') {
					arrowStyles = {
						borderLeft: `${ARROW_SIZE_PX}px solid transparent`,
						borderRight: `${ARROW_SIZE_PX}px solid transparent`,
						borderBottom: `${ARROW_SIZE_PX}px solid var(--color-site-background)`,
						top: arrowOffset * -1,
						left,
					};
				}
				if (position === 'top') {
					arrowStyles = {
						borderLeft: `${ARROW_SIZE_PX}px solid transparent`,
						borderRight: `${ARROW_SIZE_PX}px solid transparent`,
						borderTop: `${ARROW_SIZE_PX}px solid var(--color-site-background)`,
						bottom: arrowOffset * -1,
						left,
					};
				}

				return (
					<div
						className={[styles.arrowContainer, styles[position]].join(' ')}
						ref={popoverContentRef}
					>
						<div className={styles.arrow} style={arrowStyles} />
						<div
							className={styles.content}
							style={{ maxHeight: `${popoverMaxHeightRef.current}px` }}
						>
							<ScrollableContainer>
								<>
									<RichText value={content} />
									<span className={styles.citation}>
										<RichText value={citation} />
									</span>
								</>
							</ScrollableContainer>
						</div>
					</div>
				);
			}}
		>
			<HotSpotButton id={id} popupExpanded={isPopoverOpen} />
		</Popover>
	);
}

function HotSpotButton({ ref, id, popupExpanded = false }: HotSpotButtonProps) {
	return (
		<div
			ref={ref}
			className={styles.hotspotButton}
			role="button"
			aria-describedby={`tooltip-${id}`}
			aria-expanded={popupExpanded}
			aria-haspopup="dialog"
			tabIndex={0}
		></div>
	);
}

interface HotSpotVariantProps {
	id: string;
	content: PortableTextBlock[];
	citation: PortableTextBlock[];
	popoverBoundaryElement: HTMLElement | null;
	containerEl: HTMLDivElement;
	isActiveComment?: boolean;
}

type HotSpotProps = HotSpotVariantProps & {
	isMobile: boolean;
};

interface HotSpotButtonProps {
	id: string;
	popupExpanded?: boolean;
	ref?: Ref<HTMLDivElement>;
}
